import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationAttemptKind,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useExportExternalRedrive,
  useLinkExternalRedrive,
} from "../../hooks/useInvestigations";

const KIND_COPY: Record<
  InvestigationAttemptKind,
  { title: string; page: string; run: string }
> = {
  REDRIVE: {
    title: "DLQ redrive",
    page: "DLQ Redrive",
    run: "Upload the file and start the redrive.",
  },
  DELETE: {
    title: "Delete without redrive",
    page: "DLQ Redrive",
    run: "Upload the file, create and get the MCM approved, then execute the deletion.",
  },
  DOCUMENT_REDRIVE: {
    title: "Order redrive",
    page: "Redrive Tool",
    run: "Upload the file and start the redrive.",
  },
};

/**
 * A pasted Lusca Search execution page URL (DLQ Redrive `?executionArn=`, Redrive Tool
 * `?executionId=`) becomes the execution it names; anything else is sent as typed.
 */
export const executionReference = (value: string): string => {
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    const params = new URL(trimmed).searchParams;
    return (
      params.get("executionArn")?.trim() ||
      params.get("executionId")?.trim() ||
      trimmed
    );
  } catch {
    return trimmed;
  }
};

interface ExternalRedriveModalProps {
  stage: Stage;
  investigation: Investigation;
  /** Which redrive to run in Lusca Search Prod; null = closed. */
  kind: InvestigationAttemptKind | null;
  onDismiss: () => void;
  onError: (message: string) => void;
}

/**
 * Temporary until the tool runs in prod: this deployment cannot redrive the stage, so the
 * redrive is run in Lusca Search Prod with a file exported from here, and the resulting
 * execution is linked back so the case tracks it like any other attempt.
 */
export const ExternalRedriveModal: React.FC<ExternalRedriveModalProps> = ({
  stage,
  investigation,
  kind,
  onDismiss,
  onError,
}) => {
  const exporter = useExportExternalRedrive(stage);
  const link = useLinkExternalRedrive(stage);
  const [execution, setExecution] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  useEffect(() => {
    if (kind) {
      setExecution("");
      setLinkError(null);
      exporter.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when (re)opened
  }, [kind]);

  if (!kind) return null;
  const copy = KIND_COPY[kind];
  const file = exporter.data;
  const prodUrl = investigation.externalRedriveUrl ?? "Lusca Search Prod";

  const download = () =>
    exporter
      .mutateAsync({ investigationId: investigation.id, kind })
      .then((result) => window.open(result.downloadUrl, "_blank"))
      .catch((err: InvestigationApiError) => onError(err.message));

  const submit = () => {
    setLinkError(null);
    link
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        kind,
        executionArn: executionReference(execution),
      })
      .then(onDismiss)
      .catch((err: InvestigationApiError) => setLinkError(err.message));
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header={`${copy.title} in Lusca Search Prod`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!execution.trim()}
              loading={link.isLoading}
              onClick={submit}
            >
              Link redrive
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box color="text-body-secondary">
          This tool can&apos;t redrive {stage} yet, so run it in Lusca Search
          Prod and link it back here.
        </Box>
        <SpaceBetween size="xs">
          <Box variant="h4">1. Download the file</Box>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Button
              iconName="download"
              loading={exporter.isLoading}
              onClick={() => {
                download().catch(console.error);
              }}
            >
              Download{" "}
              {kind === "DOCUMENT_REDRIVE" ? "transactions" : "DLQ items"} CSV
            </Button>
            {file && (
              <StatusIndicator type="success">
                {file.count.toLocaleString()}{" "}
                {kind === "DOCUMENT_REDRIVE" ? "transactions" : "items"}
              </StatusIndicator>
            )}
          </SpaceBetween>
        </SpaceBetween>
        <SpaceBetween size="xs">
          <Box variant="h4">2. Run it in Lusca Search Prod</Box>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Button
              iconName="external"
              iconAlign="right"
              href={file?.targetUrl ?? prodUrl}
              target="_blank"
            >
              Open {copy.page}
            </Button>
            <Box color="text-body-secondary" fontSize="body-s">
              {copy.run}
            </Box>
          </SpaceBetween>
        </SpaceBetween>
        <SpaceBetween size="xs">
          <Box variant="h4">3. Link it back</Box>
          <FormField
            label="Execution link, ID or ARN"
            description="Paste the execution page URL from Lusca Search Prod, or its ID/ARN."
            errorText={linkError ?? undefined}
          >
            <Input
              value={execution}
              placeholder="e.g. https://lusca-search…/dlq-redrive?…&executionArn=…"
              onChange={({ detail }) => setExecution(detail.value)}
            />
          </FormField>
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );
};

export default ExternalRedriveModal;
